// IndexedDBSaver：把 LangGraph checkpoint 存进浏览器 IndexedDB。
//
// 参考 @langchain/langgraph-checkpoint 的 MemorySaver，把内存 Map 换成 IndexedDB。
// deepagents 的 agent 编排用它做「前端自闭环」的会话持久化，无需任何后端数据库。

import { BaseCheckpointSaver, WRITES_IDX_MAP, copyCheckpoint, getCheckpointId } from '@langchain/langgraph-checkpoint';
import * as db from './db.js';

const CHECKPOINTS = 'checkpoints';
const WRITES = 'writes';

// 与 MemorySaver._generateKey 一致：数组 JSON 化为字符串，避免原型污染。
const keyOf = (...parts) => JSON.stringify(parts);

export class IndexedDBSaver extends BaseCheckpointSaver {
    constructor() {
        super(); // 使用默认 JSON serde
    }

    // ---------- 内部辅助 ----------

    /** 反序列化写入记录为 [taskId, channel, value] 列表。 */
    async _collectWrites(threadId, checkpointNs, checkpointId) {
        const records = [];
        await db.each(WRITES, (_key, value) => {
            if (value.threadId !== threadId || value.checkpointNs !== checkpointNs || value.checkpointId !== checkpointId) return;
            records.push(value);
        });
        records.sort((a, b) => (a.taskId === b.taskId ? a.idx - b.idx : a.taskId < b.taskId ? -1 : 1));
        const pendingWrites = [];
        for (const record of records) {
            pendingWrites.push([record.taskId, record.channel, await this.serde.loadsTyped('json', record.value)]);
        }
        return pendingWrites;
    }

    /** 取某 thread/ns 下最新的 checkpoint（checkpoint_id 字符串最大者）。 */
    async _latest(threadId, checkpointNs) {
        let latest = null;
        await db.each(CHECKPOINTS, (_key, value) => {
            if (value.threadId !== threadId || value.checkpointNs !== checkpointNs) return;
            if (!latest || value.checkpointId > latest.checkpointId) latest = value;
        });
        return latest;
    }

    /** 反序列化一条 checkpoint 记录为 CheckpointTuple。 */
    async _toTuple(record, checkpointNs) {
        const { threadId, checkpointId, checkpoint, metadata, parentCheckpointId } = record;
        const checkpointNsResolved = checkpointNs ?? record.checkpointNs ?? '';
        const tuple = {
            config: { configurable: { thread_id: threadId, checkpoint_ns: checkpointNsResolved, checkpoint_id: checkpointId } },
            checkpoint: await this.serde.loadsTyped('json', checkpoint),
            metadata: await this.serde.loadsTyped('json', metadata),
            pendingWrites: await this._collectWrites(threadId, checkpointNsResolved, checkpointId),
        };
        if (parentCheckpointId !== undefined && parentCheckpointId !== null) {
            tuple.parentConfig = { configurable: { thread_id: threadId, checkpoint_ns: checkpointNsResolved, checkpoint_id: parentCheckpointId } };
        }
        return tuple;
    }

    // ---------- BaseCheckpointSaver 抽象方法 ----------

    async getTuple(config) {
        const threadId = config.configurable?.thread_id;
        const checkpointNs = config.configurable?.checkpoint_ns ?? '';
        const checkpointId = getCheckpointId(config);
        if (threadId === undefined) return undefined;

        let record;
        if (checkpointId) {
            record = await db.get(CHECKPOINTS, keyOf(threadId, checkpointNs, checkpointId));
            if (!record) return undefined;
        } else {
            record = await this._latest(threadId, checkpointNs);
            if (!record) return undefined;
        }
        return this._toTuple(record, checkpointNs);
    }

    async *list(config, options) {
        const { before, limit, filter } = options ?? {};
        const configThreadId = config.configurable?.thread_id;
        const configCheckpointNs = config.configurable?.checkpoint_ns;
        const configCheckpointId = config.configurable?.checkpoint_id;

        // 收集并按 thread/ns/checkpoint_id 排序（checkpoint_id 降序）。
        const grouped = new Map();
        await db.each(CHECKPOINTS, (_key, value) => {
            if (configThreadId !== undefined && value.threadId !== configThreadId) return;
            if (configCheckpointNs !== undefined && value.checkpointNs !== configCheckpointNs) return;
            if (configCheckpointId && value.checkpointId !== configCheckpointId) return;
            if (before?.configurable?.checkpoint_id && value.checkpointId >= before.configurable.checkpoint_id) return;
            const key = `${value.threadId}\n${value.checkpointNs}`;
            let list = grouped.get(key);
            if (!list) grouped.set(key, list = []);
            list.push(value);
        });

        const keys = [...grouped.keys()].sort();
        let remaining = limit;
        for (const key of keys) {
            const records = grouped.get(key).sort((a, b) => b.checkpointId.localeCompare(a.checkpointId));
            for (const record of records) {
                const metadata = await this.serde.loadsTyped('json', record.metadata);
                if (filter && !Object.entries(filter).every(([k, v]) => metadata[k] === v)) continue;
                if (remaining !== undefined) {
                    if (remaining <= 0) return;
                    remaining -= 1;
                }
                yield await this._toTuple(record);
            }
        }
    }

    async put(config, checkpoint, metadata) {
        const preparedCheckpoint = copyCheckpoint(checkpoint);
        const threadId = config.configurable?.thread_id;
        const checkpointNs = config.configurable?.checkpoint_ns ?? '';
        if (threadId === undefined) {
            throw new Error('Failed to put checkpoint: RunnableConfig is missing configurable.thread_id');
        }
        const checkpointId = preparedCheckpoint.id;
        const [[, serializedCheckpoint], [, serializedMetadata]] = await Promise.all([
            this.serde.dumpsTyped(preparedCheckpoint),
            this.serde.dumpsTyped(metadata),
        ]);
        await db.put(CHECKPOINTS, {
            threadId,
            checkpointNs,
            checkpointId,
            checkpoint: serializedCheckpoint,
            metadata: serializedMetadata,
            parentCheckpointId: config.configurable?.checkpoint_id ?? null,
        }, keyOf(threadId, checkpointNs, checkpointId));
        return { configurable: { thread_id: threadId, checkpoint_ns: checkpointNs, checkpoint_id: checkpointId } };
    }

    async putWrites(config, writes, taskId) {
        const threadId = config.configurable?.thread_id;
        const checkpointNs = config.configurable?.checkpoint_ns ?? '';
        const checkpointId = config.configurable?.checkpoint_id;
        if (threadId === undefined) {
            throw new Error('Failed to put writes: RunnableConfig is missing configurable.thread_id');
        }
        if (checkpointId === undefined) {
            throw new Error('Failed to put writes: RunnableConfig is missing configurable.checkpoint_id');
        }
        for (let idx = 0; idx < writes.length; idx += 1) {
            const [channel, value] = writes[idx];
            const writeIdx = WRITES_IDX_MAP[channel] ?? idx;
            const [, serializedValue] = await this.serde.dumpsTyped(value);
            await db.put(WRITES, {
                threadId,
                checkpointNs,
                checkpointId,
                taskId,
                idx: writeIdx,
                channel,
                value: serializedValue,
            }, keyOf(threadId, checkpointNs, checkpointId, taskId, writeIdx));
        }
    }

    async deleteThread(threadId) {
        const checkpointKeys = [];
        await db.each(CHECKPOINTS, (key, value) => {
            if (value.threadId === threadId) checkpointKeys.push(key);
        });
        for (const key of checkpointKeys) await db.del(CHECKPOINTS, key);

        const writeKeys = [];
        await db.each(WRITES, (key, value) => {
            if (value.threadId === threadId) writeKeys.push(key);
        });
        for (const key of writeKeys) await db.del(WRITES, key);
    }
}

export default IndexedDBSaver;
